import React, { useCallback, useRef, useState } from "react";
import { Badge, Col, Row, Spinner } from "react-bootstrap";

import DstButton from "../../components/DstButton/DstButton";
import DstTable from "../../components/DstTable/DstTable";
import { useSession } from "../../contexts/SessionContext";
import translate from "../../services/Translate";
import { ModuleImportRow } from "./ModuleUserImport.d";
import { REQUIRED_COLUMNS, parseCsvFile, submitModulesImport } from "./ModuleUserImport.function";

const ModuleUserImport = () => {
  const { language, showAlert, popAlert } = useSession();
  const [csvData, setCsvData] = useState<ModuleImportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingCell, setEditingCell] = useState<{
    rowIndex: number;
    columnKey: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const onFileUpload = useCallback(
    (file: File) => {
      parseCsvFile(file, setCsvData, setLoading, language);
    },
    [language]
  );

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const file = event.dataTransfer?.files[0];
      if (file) onFileUpload(file);
    },
    [onFileUpload]
  );

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => event.preventDefault();
  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) onFileUpload(file);
  };

  const _submitModulesCsvFile = () => {
    submitModulesImport(
      csvData,
      setSubmitting,
      fileInputRef,
      setCsvData,
      REQUIRED_COLUMNS,
      language,
      showAlert,
      popAlert
    );
  };

  const _onCellChange = (rowIndex: number, columnKey: string, value: string) => {
    const newData = [...csvData];
    newData[rowIndex] = { ...newData[rowIndex], [columnKey]: value };
    setCsvData(newData);
    setEditingCell(null);
  };

  const columns = [
    {
      name: translate(language, "PAGES.MODULE_IMPORT.TABLE.USER_EMAILS"),
      key: "user_emails",
      render: (row: ModuleImportRow) =>
        editingCell?.rowIndex === row._index && editingCell?.columnKey === "user_emails" ? (
          <input
            type="text"
            defaultValue={row.user_emails}
            onBlur={(event) => _onCellChange(row._index!, "user_emails", event.target.value)}
            autoFocus
          />
        ) : (
          <div
            className="d-flex flex-wrap align-items-center gap-1 clickable"
            onClick={() => setEditingCell({ rowIndex: row._index!, columnKey: "user_emails" })}
          >
            {row.user_emails.split(",").map((email: string) => (
              <Badge key={email.trim()} className="me-1 xs-regular lh-1">
                {email.trim()}
              </Badge>
            ))}
            <span className="dst-icon-edit-02 dropdown-icon" />
          </div>
        ),
    },
    {
      name: translate(language, "PAGES.MODULE_IMPORT.TABLE.MODULE_IDS"),
      key: "module_ids",
      render: (row: ModuleImportRow) =>
        editingCell?.rowIndex === row._index && editingCell?.columnKey === "module_ids" ? (
          <input
            type="text"
            defaultValue={row.module_ids}
            onBlur={(event) => _onCellChange(row._index!, "module_ids", event.target.value)}
            autoFocus
          />
        ) : (
          <div
            className="d-flex flex-wrap align-items-center gap-1 clickable"
            onClick={() => setEditingCell({ rowIndex: row._index!, columnKey: "module_ids" })}
          >
            {row.module_ids.split(",").map((id: string) => (
              <Badge bg="info" key={id.trim()} className="me-1 xs-regular lh-1">
                {id.trim()}
              </Badge>
            ))}
            <span className="dst-icon-edit-02 dropdown-icon" />
          </div>
        ),
    },
  ];

  return (
    <div className="p-3 w-100" id="module-import">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex">
              <h2 className="mb-0">{translate(language, "PAGES.MODULE_IMPORT.TITLE")}</h2>
            </div>
            <div className="d-flex gap-2">
              {csvData.length > 0 && (
                <DstButton
                  btnImageBefore={<span className="dst-icon-upload-03 me-2" />}
                  value={translate(language, "PAGES.MODULE_IMPORT.NEW_CSV")}
                  variant="light"
                  clickFunction={() => fileInputRef.current?.click()}
                />
              )}
              <DstButton
                btnImageBefore={<span className="dst-icon-download-02 me-2" />}
                value={translate(language, "PAGES.MODULE_IMPORT.DOWNLOAD_TEMPLATE")}
                variant="light"
                clickFunction={() => {
                  const link = document.createElement("a");
                  link.href = "/share/modules_user_template.csv";
                  link.download = "modules_import_template.csv";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              />
              <DstButton
                btnImageAfter={<span className="dst-icon-arrow-right ms-2" />}
                value={translate(language, "PAGES.MODULE_IMPORT.SUBMIT")}
                variant="primary"
                loading={submitting}
                disabled={submitting || csvData.length === 0}
                clickFunction={_submitModulesCsvFile}
              />
            </div>
          </div>
        </Col>
      </Row>
      {loading ? (
        <div className="p-3 w-100 d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
          <Spinner animation="border" role="status" />
        </div>
      ) : csvData.length === 0 ? (
        <React.Fragment>
          <DstTable data={csvData.map((item, index) => ({ ...item, _index: index }))} columns={columns} />
          <div
            className="border-2 border-dashed rounded-3 p-5 text-center d-flex flex-column align-items-center clickable"
            onDragOver={handleDragOver}
            onDrop={onDrop}
          >
            <div className="s-regular mb-2">{translate(language, "PAGES.MODULE_IMPORT.DROP_HERE")}</div>
            <DstButton
              value={translate(language, "PAGES.MODULE_IMPORT.UPLOAD_LOCAL_FILE")}
              variant="link"
              btnClass="ps-1"
              clickFunction={() => fileInputRef.current?.click()}
            />
          </div>
        </React.Fragment>
      ) : (
        <DstTable
          data={csvData.map((item, index) => ({ ...item, _index: index }))}
          columns={columns}
          itemsPerPage={20}
        />
      )}

      <input type="file" ref={fileInputRef} className="d-none" accept=".csv" onChange={handleFileInputChange} />
    </div>
  );
};

export default ModuleUserImport;
